import React from "react"
import { graphql } from "gatsby"
import { Layout, Head as HeadComponent } from "@bw/layouts"
import { Spacer, Section } from "@bw/bits"
import { Cartouche } from "@bw/partials"
import { useTranslation } from "react-i18next"
import { PillMarker } from "@bw/icons"
import PoiModel from "models/poi"
import { Header, Content } from "@bw/modules/mapModules"
import { useBreadcrumbs } from "../hooks/useBreadcrumbs"

export const Head = ({ location, pageContext, data }) => (
  <HeadComponent
    title={data.page.metaTitle || data.page.title}
    image={
      data.page.image?.localImage?.childImageSharp?.original?.src ??
      data.page.teaserImage?.localImage?.childImageSharp?.original?.src
    }
    data={data}
    location={location}
    pageContext={pageContext}
    description={data.page.metaDescription}
    doNotIndex={data.page.doNotIndex}
  />
)

const PoiTemplate = ({ data, pageContext, location }) => {
  const { t } = useTranslation()
  const { page, parents, mount } = data
  const Poi = new PoiModel(page)

  const breadcrumbsLinks = useBreadcrumbs({
    mount,
    page,
    parents: pageContext.parents.map(id =>
      parents.nodes.find(parent => parent.id === id)
    ),
  })

  const [line1, line2] = Poi.getAddressLines()
  const todayOpeningHours = Poi.getTodayOpeningHours()

  const address = (
    <Spacer direction="row">
      <PillMarker />
      <address>
        {line1}
        <br />
        {line2}
      </address>
    </Spacer>
  )

  return (
    <Layout {...{ pageContext, location }}>
      {/*
       * Loading the Header module, ex Hero or poiHeader
       */}
      <Header
        modules={Poi.header}
        page={Poi}
        pageContext={pageContext}
        breadcrumbsLinks={breadcrumbsLinks}
      />
      <Section classAttribute="poi-cartouche">
        <Cartouche>
          {Poi.hasGeoPosition() ? (
            <a
              href={`https://maps.google.com/?q=${Poi.latitude},${Poi.longitude}`}
              target="_blank"
              rel="noreferrer"
            >
              {address}
            </a>
          ) : (
            address
          )}
          {(Poi.email || Poi.phone) && (
            <div>
              {Poi.email && (
                <div>
                  <a href={`mailto:${Poi.email}`}>{Poi.email}</a>
                </div>
              )}
              {Poi.phone && (
                <div>
                  <a href={`tel:${Poi.phone}`}>{Poi.phone}</a>
                </div>
              )}
            </div>
          )}
          {Poi.hasOpeningHours() && (
            <details>
              <summary>
                <span style={{ fontWeight: "500" }}>{t("dates:today")}:</span>{" "}
                {PoiModel.formatOpeningHours(todayOpeningHours) || t("closed")}
              </summary>
              {Object.keys(Poi.openingHoursByDay).map(day => (
                <div style={{ marginTop: "5px" }}>
                  <span style={{ fontWeight: "500" }}>
                    {t(`dates:${day}`)}:{" "}
                  </span>
                  {PoiModel.formatOpeningHours(Poi.openingHoursByDay[day]) ||
                    t("closed")}
                </div>
              ))}
            </details>
          )}
        </Cartouche>
      </Section>
      {/*
       * Loading the Contents modules, ex Content or CallToAction, TeasersLatest
       */}
      <Content modules={Poi.content} />
    </Layout>
  )
}

export default PoiTemplate

export const poiQuery = graphql`
  query poiQuery($id: String!, $parents: [String]) {
    parents: allCollectionPages(filter: { id: { in: $parents } }) {
      nodes {
        id
        to: slug
        title
      }
    }

    page: collectionPois(id: { eq: $id }) {
      title
      metaTitle
      metaDescription
      image {
        localImage {
          childImageSharp {
            original {
              src
            }
          }
        }
      }
      doNotIndex

      teaserImage {
        localImage {
          childImageSharp {
            original {
              src
            }
          }
        }
      }

      street
      city
      zipCode
      state
      country
      phone
      email
      displayOnMap
      latitude
      longitude
      openingHours {
        days {
          value
        }
        periods {
          from
          to
        }
      }

      header {
        __typename
        suptitle
        title
        subtitle

        ...HeroModuleFragment
      }
      content {
        __typename
        suptitle
        title
        subtitle
        backgroundVariant
        buttonType {
          value
        }
        buttonUrl
        buttonIsExternal
        buttonOpenInModal
        buttonText
        buttonPosition {
          value
        }
        buttonEntry {
          url
        }

        ...AudioModuleFragment
        ...CallToActionModuleFragment
        ...ContentModuleFragment
        ...ContentWithImageModuleFragment
        ...GalleryModuleFragment
        ...ContentOnColumnsModuleFragment
        ...DownloadsModuleFragment
        ...TeasersManualModuleFragment
        ...LogoGridModuleFragment
        ...ImageModuleFragment
        ...VideoModuleFragment
        ...FormModuleFragment
        ...ListOfPoisModuleFragment
        ...ListOfArticlesModuleFragment
        ...ListOfProfilesModuleFragment
        ...ListOfJobsModuleFragment
        ...ListOfEventsModuleFragment
        ...LinksModuleFragment
        ...PredefinedModuleModuleFragment
      }
    }

    mount: collectionPages(mount: { handle: { eq: "pois" } }) {
      slug
      title
    }
  }
`
